import { MessageBarType, PanelType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  iconButtonStylesNoBG,
  IPageHeaderProps,
  IPanelProps,
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBarList,
  messageBarListStylesToast,
  PageHeader,
  pageHeaderStylesSecondary,
  Panel,
  useClassNames,
  useMessageBarList,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  DraftToFeatureSetRequest,
  FeatureSetDraftResponse,
  FeatureSetDraftType,
  ListableFeatureSet,
  ListableFeatureSetResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  ContentsTitle,
  defaultPanelWidth,
  DraftToFeatureSet,
  FeatureSchemaDraftWithDepth,
  FeatureSetAction,
} from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { RegisterFeatureSetAddSchema } from '../RegisterFeatureSetAddSchema/RegisterFeatureSetAddSchema';
import { RegisterFeatureSetSelectProject } from '../RegisterFeatureSetSelectProject/RegisterFeatureSetSelectProject';
import { RegisterFeatureSetSelectSchema } from '../RegisterFeatureSetSelectSchema/RegisterFeatureSetSelectSchema';
import {
  IRegisterFeatureSetPanelClassNames,
  IRegisterFeatureSetPanelStyles,
  registerFeatureSetPanelStylesDefault,
} from './RegisterFeatureSetPanel.styles';

enum RegisterStep {
  SelectProject = 'Select Project',
  SelectSchema = 'Select Schema',
  AddSchema = 'Add Schema',
}

const getBreadcrumbItems = (
  step?: RegisterStep,
  projectName = '',
  featureSetName = '',
  title: ContentsTitle = ContentsTitle.registerFeatureSet
) => {
  const step1 = {
    text: ContentsTitle.selectCreateProject,
    key: ContentsTitle.selectCreateProject,
  };
  const step1Project = { text: `Project: ${projectName}`, key: projectName };
  const step2 = { text: title, key: title };
  const step2FeatureSet = { text: `Feature Set: ${featureSetName}`, key: featureSetName };
  const step3 = { text: ContentsTitle.addSchema, key: ContentsTitle.addSchema };
  switch (step) {
    case RegisterStep.SelectSchema:
      return [step1Project, { ...step2, isCurrentItem: true }];
    case RegisterStep.AddSchema:
      return [step1Project, step2FeatureSet, { ...step3, isCurrentItem: true }];
    default:
      return [{ ...step1, isCurrentItem: true }];
  }
};

const getPanelWidth = (step?: RegisterStep) =>
  step === RegisterStep.AddSchema ? `${window.innerWidth - 97}px` : defaultPanelWidth;

const getTitle = (action: FeatureSetAction = FeatureSetAction.register, isNewVersionDraft?: boolean): ContentsTitle => {
  switch (action) {
    case FeatureSetAction.newVersion:
      return ContentsTitle.createNewMajorVersion;
    case FeatureSetAction.draft:
      return isNewVersionDraft ? ContentsTitle.createNewMajorVersion : ContentsTitle.registerFeatureSet;
    default:
      return ContentsTitle.registerFeatureSet;
  }
};

interface UpdateInfo {
  id?: string;
  version?: string;
  backfill?: boolean;
  extractSchema?: boolean;
  action?: FeatureSetAction;
}

interface IRegisterFeatureSetPanelProps {
  action?: FeatureSetAction;
  featureSetDraftId?: string;
  featureSetId?: string;
  projectId?: string;
  projectName?: string;
  show: boolean;
  onDismiss: (updated?: boolean, updateInfo?: UpdateInfo) => void;
}

export const RegisterFeatureSetPanel = ({
  action = FeatureSetAction.register,
  show,
  featureSetId,
  featureSetDraftId,
  projectId,
  projectName,
  onDismiss,
}: IRegisterFeatureSetPanelProps) => {
  const [isOpen, { setTrue: open, setFalse: close }] = useBoolean(false);
  const [stepAndData, setStepAndData] = useState<{
    step?: RegisterStep;
    projectId?: string;
    projectName?: string;
    featureSetId?: string;
    featureSetName?: string;
    featureSetVersion?: string;
    draftFeatureSet?: DraftToFeatureSet;
    draftFeatures?: FeatureSchemaDraftWithDepth[];
    changedFeatureSet?: boolean;
    isNewVersionDraft?: boolean;
    latestFeatureSet?: ListableFeatureSet;
  }>({});
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const { messages, addMessage, removeMessage, removeAllMessages } = useMessageBarList();
  const refMeta = useRef<{ title: ContentsTitle }>({ title: getTitle(action) });
  const history = useHistory();
  const { addToast } = useToast();
  // When creating version, we need the latest feature set version.
  const loadLatestFeatureSet = useCallback(
    async (featureSetId?: string) => {
      setLoadingMessage('loading the latest feature set');
      try {
        // If request doesn't have a featureSetVerion, it will return the latest feature set.
        const { listableFeatureSet = {} }: ListableFeatureSetResponse = await fsApi(CoreService.GetListableFeatureSet, {
          featureSetId,
        });
        const {
          projectId,
          projectName,
          version,
          name,
          timeTravelColumn: timeTravelColumnName,
          timeTravelColumnFormat,
          partitionBy = [],
        } = listableFeatureSet;
        setStepAndData({
          ...stepAndData,
          step: RegisterStep.SelectSchema,
          projectId,
          projectName,
          featureSetId,
          featureSetVersion: version,
          featureSetName: name,
          latestFeatureSet: listableFeatureSet,
          draftFeatureSet: {
            ...stepAndData.draftFeatureSet,
            timeTravelColumn: {
              timeTravelColumnName,
              timeTravelColumnFormat,
              isPartitionColumn: !!timeTravelColumnName || partitionBy.includes('time_travel_column_auto_generated'),
            },
          },
        });
        setLoadingMessage('');
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Feature Set: `));
        setLoadingMessage('');
        onClose();
      }
    },
    [addToast, stepAndData]
  );
  const loadFeatureSetDraft = useCallback(
    async (featureSetDraftId?: string) => {
      setLoadingMessage('loading the feature set draft');
      try {
        const { featureSetDraft = {} }: FeatureSetDraftResponse = await fsApi(CoreService.GetFeatureSetDraft, {
          featureSetDraftId,
        });

        const { projectId, draftType, name, featureSetId, featureSetVersion } = featureSetDraft;
        let latestVersion = featureSetVersion;
        const isNewVersionDraft = draftType === FeatureSetDraftType.FEATURE_SET_DRAFT_TYPE_CREATE_MAJOR_VERSION;
        if (isNewVersionDraft) {
          // get the latest version again since the feature set draft can have an old version
          const { listableFeatureSet = {} }: ListableFeatureSetResponse = await fsApi(
            CoreService.GetListableFeatureSet,
            {
              featureSetId,
            }
          );
          const { version } = listableFeatureSet;
          latestVersion = version;
        }
        setStepAndData({
          ...stepAndData,
          step: RegisterStep.AddSchema,
          isNewVersionDraft,
          changedFeatureSet: true,
          featureSetId,
          featureSetName: name,
          featureSetVersion: latestVersion,
          projectId,
          draftFeatureSet: { ...featureSetDraft },
        });
        refMeta.current.title = getTitle(isNewVersionDraft ? FeatureSetAction.newVersion : FeatureSetAction.register);
        setLoadingMessage('');
        // if it is empty, it is an error. for issue 2545
        if (!featureSetDraft.featureSetDraftId) {
          addToast({
            messageBarType: MessageBarType.error,
            message: `There is no feature set draft (id: ${featureSetDraftId})`,
          });
          onClose();
        }
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Feature Set Draft: `));
        setLoadingMessage('');
        onClose();
      }
    },
    [addToast, stepAndData]
  );
  const onClose = useCallback((updated?: boolean, updateInfo?: UpdateInfo) => {
    setStepAndData({});
    removeAllMessages();
    onDismiss(updated, updateInfo);
  }, []);
  const panelProps: IPanelProps = {
    type: PanelType.custom,
    customWidth: getPanelWidth(stepAndData.step),
    isOpen,
    onOuterClick: (e) => {
      // The editor is the outside of the panel, so when clicking the editor, the panel will be closed.
      e?.preventDefault();
      e?.stopPropagation();
    },
    onDismiss: () => onClose(),
    isFooterAtBottom: true,
    hasCloseButton: false,
  };
  const pageHeaderProps: IPageHeaderProps = {
    pageTitle: refMeta.current.title,
    styles: pageHeaderStylesSecondary,
    breadcrumbProps: {
      items: getBreadcrumbItems(
        stepAndData.step,
        stepAndData.projectName,
        stepAndData.featureSetName,
        refMeta.current.title
      ),
    },
    actions: <IconButton styles={iconButtonStylesNoBG} iconName="Clear" onClick={() => onClose()} />,
  };
  useEffect(() => {
    if (show) {
      open();
      switch (action) {
        case FeatureSetAction.register:
          // from Project Details Page
          if (projectId) {
            setStepAndData({ step: RegisterStep.SelectSchema, projectId, projectName });
          } else {
            // from scratch
            setStepAndData({ step: RegisterStep.SelectProject });
          }
          break;
        case FeatureSetAction.newVersion:
          // from Feature Set Details. skip SelectProject
          loadLatestFeatureSet(featureSetId);
          break;
        case FeatureSetAction.draft:
          // From On going feature sets. skip SelectProject, SelectSchema
          loadFeatureSetDraft(featureSetDraftId);
          break;
      }
    } else {
      close();
    }
  }, [show, action, projectId, featureSetDraftId, featureSetId]);
  const classNames = useClassNames<IRegisterFeatureSetPanelStyles, IRegisterFeatureSetPanelClassNames>(
    'RegisterFeatureSetPanel',
    registerFeatureSetPanelStylesDefault
  );
  return stepAndData.step ? (
    <Panel {...panelProps}>
      <div className={classNames.root} data-test="register-feature-set">
        <div className={classNames.messageBar}>
          <MessageBarList
            styles={messageBarListStylesToast}
            messages={messages}
            autoClose
            groupByMessage
            onRemoveMessage={removeMessage}
          />
        </div>
        <PageHeader {...pageHeaderProps} />
        {stepAndData.step === RegisterStep.SelectProject ? (
          <RegisterFeatureSetSelectProject
            onDismiss={onClose}
            onSelect={(project, msg) => {
              if (project) {
                setStepAndData({ step: RegisterStep.SelectSchema, projectId: project.id, projectName: project.name });
              }
              if (msg) {
                const { messageBarType, message } = msg;
                addMessage(message as string, undefined, undefined, messageBarType);
              }
            }}
          />
        ) : null}
        {stepAndData.step === RegisterStep.SelectSchema && stepAndData.projectId ? (
          <RegisterFeatureSetSelectSchema
            action={action}
            projectId={stepAndData.projectId}
            featureSetId={stepAndData.featureSetId}
            featureSetVersion={stepAndData.featureSetVersion}
            featureSetName={stepAndData.featureSetName}
            draftFeatureSet={stepAndData.draftFeatureSet}
            isNewVersionDraft={stepAndData.isNewVersionDraft}
            changedFeatureSet={stepAndData.changedFeatureSet}
            onCancel={() => onClose()}
            onCreate={() => onClose(true, { extractSchema: true })}
            onMoveNext={(draftFeatureSet?: DraftToFeatureSetRequest) => {
              if (draftFeatureSet) {
                setStepAndData({
                  ...stepAndData,
                  step: RegisterStep.AddSchema,
                  draftFeatureSet,
                  changedFeatureSet: true,
                });
              }
            }}
          />
        ) : null}
        {stepAndData.step === RegisterStep.AddSchema && stepAndData.draftFeatureSet ? (
          <RegisterFeatureSetAddSchema
            action={action}
            featureSetDraftId={featureSetDraftId}
            featureSetId={stepAndData.featureSetId}
            featureSetVersion={stepAndData.featureSetVersion}
            draftFeatureSet={stepAndData.draftFeatureSet}
            draftFeatures={stepAndData.draftFeatures}
            changedFeatureSet={stepAndData.changedFeatureSet}
            isNewVersionDraft={stepAndData.isNewVersionDraft}
            latestFeatureSet={stepAndData.latestFeatureSet}
            onSave={(id, version, backfill) => {
              if (action === FeatureSetAction.register) {
                onClose();
                history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${id}`);
              } else {
                if (action === FeatureSetAction.draft && stepAndData.isNewVersionDraft) {
                  onClose();
                  history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${id}/version/${version}`);
                } else {
                  onClose(!!id, { id, version, backfill, action });
                }
              }
            }}
            onMovePrevious={(
              draftFeatureSet: DraftToFeatureSetRequest,
              draftFeatures?: FeatureSchemaDraftWithDepth[]
            ) => {
              setStepAndData({ ...stepAndData, step: RegisterStep.SelectSchema, draftFeatureSet, draftFeatures });
            }}
            onCancel={onClose}
          />
        ) : null}
      </div>
      {loadingMessage && (
        <Loader styles={[loaderStylesSpinnerXLarge, { root: { height: '100%' } }]} label={loadingMessage} />
      )}
    </Panel>
  ) : null;
};
